'use client'

import { useEffect, useMemo, useRef, useState } from "react"
import { usePathname } from "next/navigation"
import { formatDateNormal, formatImageURL, formatNumber, isLoyaltyActive } from "@/utils/util"
import { ArrowDownUp, ImagePlus, MessageCircleX, Star, Trash, X } from "lucide-react"
import { navigateUrl, sendClientRequest } from "@/utils/client-request"
import TextEditorMininal from "@/components/elements/TextEditorMinimal"
import EmptyState from "../elements/EmptyState"
import ImageCarousel from "@/components/blocks/ImageCarousel"
import { useCart, useProductReviews, type ReviewFilterId } from "@/lib/commerce/hooks"

const focusRing = 'focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-foreground'
const primaryButton = `inline-flex items-center justify-center h-11 px-8 bg-foreground text-background text-[13px] tracking-wide transition-opacity duration-150 hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed ${focusRing}`
const secondaryButton = `inline-flex items-center justify-center gap-2 h-11 px-6 border border-foreground text-foreground text-[13px] tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background disabled:opacity-50 disabled:cursor-not-allowed ${focusRing}`
const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'

interface ReviewProps {
  product: any
  vendor: any
  reviews: {
    counts: { rating: number; count: number }[]
    data: any[]
    totalCount: number
    average: number
    userReviews?: any[]
  }
  canReview?: boolean
}

function Stars({ value, size = 'size-3.5' }: { value: number, size?: string }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} од`}>
      {[0, 1, 2, 3, 4].map(r => (
        <Star key={r} aria-hidden className={`${size} shrink-0 ${value > r ? 'fill-foreground text-foreground' : 'fill-transparent text-foreground/20'}`} strokeWidth={1.25} />
      ))}
    </span>
  )
}

function ReviewImages({ images, onOpen }: { images: string[], onOpen: (imgs: string[], clicked: string) => void }) {
  if (!images || images.length === 0) return null
  return (
    <div className="mt-4 flex flex-wrap gap-1">
      {images.map((image, idx) => (
        <button key={idx} type="button" onClick={() => onOpen(images, image)} aria-label={`Зураг ${idx + 1} томруулах`} className={`size-20 bg-muted overflow-hidden cursor-zoom-in ${focusRing}`}>
          <img src={formatImageURL(image)} alt="" className="w-full h-full object-cover" />
        </button>
      ))}
    </div>
  )
}

export default function Review({ product, vendor, reviews: initialReviews, canReview = false }: ReviewProps) {
  const pathname = usePathname()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { loyaltySummary } = useCart()
  const [publicBasePoints, setPublicBasePoints] = useState(0)
  const reviewRewardPoints = isLoyaltyActive(vendor) ? (loyaltySummary?.currentTier?.reviewRewardPoints || 0) : 0
  const reviewRewardCapCount = loyaltySummary?.reviewRewardCapCount || 0
  const reviewRewardCapWindowDays = loyaltySummary?.reviewRewardCapWindowDays || 0
  const reviewRewardCapped = reviewRewardPoints > 0 && reviewRewardCapCount > 0 && reviewRewardCapWindowDays > 0 && (loyaltySummary?.reviewRewardUsedCount || 0) >= reviewRewardCapCount

  useEffect(() => {
    if (canReview || !isLoyaltyActive(vendor)) return
    let cancelled = false
    sendClientRequest({ url: '/loyalty/tiers', auth: false, cache: true, headers: { Vendor: vendor?.id } }).then(({ data }: any) => {
      if (cancelled || !data?.tiers?.length) return
      const sorted = [...data.tiers].sort((a: any, b: any) => a.minAnnualSpend - b.minAnnualSpend)
      setPublicBasePoints(sorted[0]?.reviewRewardPoints || 0)
    }).catch(() => { })
    return () => { cancelled = true }
  }, [canReview, vendor?.id])

  const {
    reviews,
    deletedReview, pendingReview, publishedReview, hasAnyReview,
    totalCount, photoCount, fiveStarCount,
    getFilteredReviews,
    content, setContent,
    rating, setRating,
    images,
    btnLoading,
    loadingImages,
    postReview,
    uploadImages,
    removeImage,
  } = useProductReviews({ product, initialReviews })

  const [isCarouselOpen, setIsCarouselOpen] = useState(false)
  const [carouselImages, setCarouselImages] = useState<string[]>([])
  const [activeImage, setActiveImage] = useState<string>('')
  const [activeFilter, setActiveFilter] = useState<ReviewFilterId>('all')
  const [composerOpen, setComposerOpen] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [sortNewest, setSortNewest] = useState(true)

  const filteredReviews = useMemo(() => getFilteredReviews(activeFilter, sortNewest), [reviews.data, activeFilter, sortNewest])
  const visibleReviews = showAll ? filteredReviews : filteredReviews.slice(0, 5)
  const hasMore = filteredReviews.length > 5

  if (!product) return <></>

  const handleImages = async (e: any) => {
    await uploadImages(e.target.files)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const openImageInCarousel = (imgs: string[], clickedImage: string) => {
    setCarouselImages(imgs)
    setActiveImage(clickedImage)
    setIsCarouselOpen(true)
  }

  const filters: { id: ReviewFilterId, label: string }[] = [
    { id: 'all', label: `Бүгд (${totalCount})` },
    { id: '5', label: `5 од (${fiveStarCount})` },
    { id: 'photo', label: `Зурагтай (${photoCount})` },
  ]

  const pointsNote = reviewRewardPoints > 0 && (
    <span className="inline-flex items-center h-6 px-2 border border-border text-[11px] tabular-nums text-muted-foreground whitespace-nowrap">
      {reviewRewardCapped ? 'Онооны лимит хүрсэн' : `+${formatNumber(reviewRewardPoints)} оноо`}
    </span>
  )

  const composer = (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-1" role="radiogroup" aria-label="Үнэлгээ">
          {[0, 1, 2, 3, 4].map(r => (
            <button key={r} type="button" role="radio" aria-checked={rating === r + 1} aria-label={`${r + 1} од`} onClick={() => setRating(r + 1)} className={`p-0.5 ${focusRing}`}>
              <Star className={`size-6 ${rating > r ? 'fill-foreground text-foreground' : 'fill-transparent text-foreground/25'}`} strokeWidth={1.25} />
            </button>
          ))}
        </div>
        <input type="file" accept="image/*" multiple ref={fileInputRef} className="hidden" onChange={handleImages} />
        <button type="button" onClick={() => fileInputRef.current?.click()} disabled={loadingImages} className={`inline-flex items-center gap-1.5 text-[13px] transition-opacity duration-150 hover:opacity-60 disabled:opacity-50 ${focusRing}`}>
          <ImagePlus className="size-4" strokeWidth={1.5} />
          {loadingImages ? 'Ачаалж байна…' : 'Зураг нэмэх'}
        </button>
      </div>
      <div className="p-4 border border-border min-h-32 text-sm focus-within:border-foreground transition-colors duration-150 [&_.ProseMirror]:min-h-24 [&_.ProseMirror]:outline-none">
        <TextEditorMininal content={content} setContent={setContent} />
      </div>
      {images.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {images.map((image, index) => (
            <div key={index} className="relative size-20 bg-muted">
              <button type="button" onClick={() => openImageInCarousel(images, image)} aria-label={`Зураг ${index + 1} томруулах`} className="w-full h-full cursor-zoom-in">
                <img src={formatImageURL(image)} alt="" className="w-full h-full object-cover" />
              </button>
              <button type="button" onClick={() => removeImage(image)} aria-label="Зураг устгах" className="absolute top-1 right-1 size-6 grid place-items-center bg-background text-foreground transition-opacity duration-150 hover:opacity-80">
                <Trash className="size-3" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-end">
        <button type="button" onClick={postReview} disabled={btnLoading} className={primaryButton}>
          {btnLoading ? 'Илгээж байна…' : 'Илгээх'}
        </button>
      </div>
    </div>
  )

  const prompt = canReview ? (
    !hasAnyReview && !composerOpen && (
      <div className="mt-10 pt-8 border-t border-border">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-base text-foreground">Сэтгэгдэл үлдээх үү?</h3>
          {pointsNote}
        </div>
        <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
          {reviewRewardCapped
            ? `Та ${reviewRewardCapWindowDays} хоногт ${reviewRewardCapCount} сэтгэгдэл бичиж оноо цуглуулах лимитэд хүрсэн байна. Таны энэ сэтгэгдэлд оноо олгогдохгүй ч бусад худалдан авагчдад худалдан авалт хийхэд нь туслаарай.`
            : reviewRewardPoints > 0
              ? `Үнэлгээ өгч бусад худалдан авагчдад туслаад, ${formatNumber(reviewRewardPoints)} оноо цуглуулаарай.`
              : 'Хэрэглэсэн бүтээгдэхүүнийхээ талаар үнэлгээ өгч, бусад худалдан авагчдад туслаарай.'}
        </p>
        <button type="button" onClick={() => setComposerOpen(true)} className={`mt-5 ${secondaryButton}`}>Сэтгэгдэл бичих</button>
      </div>
    )
  ) : (
    <div className="mt-10 pt-8 border-t border-border">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-base text-foreground">Нэвтэрч үнэлгээ өгөх</h3>
        {publicBasePoints > 0 && (
          <span className="inline-flex items-center h-6 px-2 border border-border text-[11px] tabular-nums text-muted-foreground whitespace-nowrap">+{formatNumber(publicBasePoints)} оноо</span>
        )}
      </div>
      <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
        {publicBasePoints > 0
          ? `Нэвтэрч сэтгэгдэл, үнэлгээ үлдээгээд ${formatNumber(publicBasePoints)} оноо цуглуулж, бусад хэрэглэгчдэд зөв сонголт хийхэд нь туслаарай.`
          : 'Хэрэглэсэн бүтээгдэхүүнийхээ талаар сэтгэгдэл, үнэлгээ үлдээж бусад хэрэглэгчдэд зөв сонголт хийхэд нь туслаарай.'}
      </p>
      <a href={navigateUrl(`/login?url=/product/${product.customId}`, pathname)} className={`mt-5 ${secondaryButton}`}>Нэвтрэх</a>
    </div>
  )

  const composerPanel = canReview && (hasAnyReview || composerOpen) && (
    <div className="mb-10 p-5 sm:p-8 border border-border">
      {hasAnyReview ? (
        <UserReviewStatus publishedReview={publishedReview} pendingReview={pendingReview} deletedReview={deletedReview} composerOpen={composerOpen} setComposerOpen={setComposerOpen} composer={composer} openImageInCarousel={openImageInCarousel} />
      ) : (
        <>
          <div className="flex items-center justify-between gap-4 mb-5">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base text-foreground">Сэтгэгдэл бичих</h3>
              {pointsNote}
            </div>
            <button type="button" onClick={() => setComposerOpen(false)} aria-label="Хаах" className={`-mr-2 size-9 grid place-items-center transition-opacity duration-150 hover:opacity-60 ${focusRing}`}>
              <X className="size-4" strokeWidth={1.5} />
            </button>
          </div>
          {composer}
        </>
      )}
    </div>
  )

  return (
    <div>
      <ImageCarousel images={carouselImages} initialImage={activeImage} isOpen={isCarouselOpen} onClose={() => setIsCarouselOpen(false)} productName={product.name} vendor={vendor} />

      <h2 className="mb-8 lg:mb-12 text-2xl sm:text-4xl tracking-tight text-foreground">Хэрэглэгчийн сэтгэгдэл</h2>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-12 lg:gap-20">
        <aside className="self-start lg:sticky lg:top-24">
          <div className="flex items-baseline gap-2">
            <span className="text-6xl font-light tracking-tight leading-none tabular-nums">{reviews.average ? Number(reviews.average).toFixed(1) : '0.0'}</span>
            <span className="text-sm text-muted-foreground tabular-nums">/ 5</span>
          </div>
          <div className="mt-3"><Stars value={Math.round(reviews.average ?? 0)} size="size-4" /></div>
          <div className="mt-2 text-[13px] text-muted-foreground">Нийт {totalCount} сэтгэгдэл</div>

          <div className="mt-8 space-y-2.5">
            {reviews.counts.slice().reverse().map(c => {
              const pct = totalCount > 0 ? Math.round((c.count / totalCount) * 100) : 0
              return (
                <div key={c.rating} className="grid grid-cols-[12px_1fr_40px] items-center gap-3 text-xs tabular-nums">
                  <span className="text-muted-foreground">{c.rating}</span>
                  <div className="h-0.5 bg-border">
                    <div className="h-full bg-foreground transition-[width] duration-500" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-right text-muted-foreground">{c.count}</span>
                </div>
              )
            })}
          </div>

          {prompt}
        </aside>

        <div className="min-w-0">
          {composerPanel}

          <div className="flex items-center gap-6 border-b border-border">
            <div className="flex items-center gap-6 overflow-x-auto scrollbar-hide">
              {filters.map(f => (
                <button key={f.id} type="button" onClick={() => { setActiveFilter(f.id); setShowAll(false) }} aria-pressed={activeFilter === f.id} className={`shrink-0 -mb-px py-3 border-b text-[13px] whitespace-nowrap transition-colors duration-150 ${activeFilter === f.id ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'} ${focusRing}`}>
                  {f.label}
                </button>
              ))}
            </div>
            <button type="button" onClick={() => setSortNewest(s => !s)} className={`ml-auto shrink-0 inline-flex items-center gap-1.5 text-[13px] text-muted-foreground transition-colors duration-150 hover:text-foreground ${focusRing}`}>
              <ArrowDownUp className="size-3.5" strokeWidth={1.5} />
              {sortNewest ? 'Шинэ нь эхэндээ' : 'Хуучин нь эхэндээ'}
            </button>
          </div>

          {filteredReviews.length > 0 ? (
            <>
              <div className="divide-y divide-border">
                {visibleReviews.map((rev: any) => (
                  <article key={rev.id} className="py-8">
                    <div className="flex items-baseline justify-between gap-4">
                      <span className="text-sm text-foreground">{rev.customer ? rev.customer?.firstName : 'Хэрэглэгч'}</span>
                      <time className="text-xs text-muted-foreground tabular-nums">{formatDateNormal(new Date(rev.createdAt))}</time>
                    </div>
                    <div className="mt-2"><Stars value={rev.rating} /></div>
                    <div className="mt-3">
                      {rev.comment ? (
                        <div className="text-sm leading-relaxed text-foreground/80 blog-detail prose prose-p:mt-0 prose-p:mb-0 max-w-2xl" dangerouslySetInnerHTML={{ __html: rev.comment }} />
                      ) : (
                        <span className="text-sm text-muted-foreground">Сэтгэгдэл хоосон байна</span>
                      )}
                    </div>
                    <ReviewImages images={rev.images || []} onOpen={openImageInCarousel} />
                  </article>
                ))}
              </div>

              {hasMore && (
                <div className="pt-6 border-t border-border flex items-center justify-between gap-4">
                  <span className="text-xs text-muted-foreground tabular-nums">{visibleReviews.length} / {filteredReviews.length} сэтгэгдэл</span>
                  <button type="button" onClick={() => setShowAll(s => !s)} className={secondaryButton}>{showAll ? 'Хураах' : 'Бүгдийг харах'}</button>
                </div>
              )}
            </>
          ) : (
            <EmptyState icon={MessageCircleX} title={activeFilter === 'all' ? 'Сэтгэгдэл хоосон байна' : 'Тохирох сэтгэгдэл олдсонгүй'} description={activeFilter === 'all' ? 'Яг одоогоор ямар нэгэн сэтгэгдэл байхгүй байна. Хэсэг хугацааны дараа дахин шалгана уу.' : 'Өөр шүүлтүүр сонгож үзнэ үү.'} />
          )}
        </div>
      </div>
    </div>
  )
}

function StatusBlock({ label, title, review, note, openImageInCarousel }: { label: string, title: string, review: any, note?: React.ReactNode, openImageInCarousel: (imgs: string[], clicked: string) => void }) {
  return (
    <div>
      <div className={eyebrow}>{label}</div>
      <div className="mt-2 text-base text-foreground">{title}</div>
      {note}
      {review.rating > 0 && (
        <div className="mt-3 flex items-center gap-2">
          <Stars value={review.rating} />
          {review.createdAt && <span className="text-xs text-muted-foreground tabular-nums">{formatDateNormal(new Date(review.createdAt))}</span>}
        </div>
      )}
      {review.comment && <div className="mt-3 text-sm leading-relaxed text-foreground/80 blog-detail prose prose-p:mt-0 prose-p:mb-0" dangerouslySetInnerHTML={{ __html: review.comment }} />}
      <ReviewImages images={review.images || []} onOpen={openImageInCarousel} />
    </div>
  )
}

function UserReviewStatus({ publishedReview, pendingReview, deletedReview, composerOpen, setComposerOpen, composer, openImageInCarousel }: { publishedReview: any, pendingReview: any, deletedReview: any, composerOpen: boolean, setComposerOpen: (v: boolean) => void, composer: React.ReactNode, openImageInCarousel: (imgs: string[], clicked: string) => void }) {
  return (
    <div className="space-y-8 divide-y divide-border [&>*+*]:pt-8">
      {publishedReview && (
        <StatusBlock label="Нийтлэгдсэн" title="Та амжилттай үнэлгээ өгсөн байна" review={publishedReview} openImageInCarousel={openImageInCarousel} />
      )}
      {pendingReview && (
        <StatusBlock label="Хянагдаж байна" title="Таны үнэлгээ хянагдаж байна" review={pendingReview} openImageInCarousel={openImageInCarousel} />
      )}
      {deletedReview && !publishedReview && (
        <div>
          <StatusBlock
            label="Татгалзсан"
            title="Таны үнэлгээг татгалзсан байна"
            review={{ createdAt: deletedReview.createdAt }}
            note={<p className="mt-1 text-[13px] text-muted-foreground">{pendingReview ? 'Засварлагдсан сэтгэгдлийг хянаж байна.' : 'Та сэтгэгдлээ дахин засварлаж илгээх боломжтой.'}{deletedReview.createdAt && <span className="ml-1.5 tabular-nums">· {formatDateNormal(new Date(deletedReview.createdAt))}</span>}</p>}
            openImageInCarousel={openImageInCarousel}
          />
          {!pendingReview && (
            composerOpen ? (
              <div className="mt-6 pt-6 border-t border-border">{composer}</div>
            ) : (
              <button type="button" onClick={() => setComposerOpen(true)} className={`mt-5 ${secondaryButton}`}>Дахин үнэлгээ өгөх</button>
            )
          )}
        </div>
      )}
    </div>
  )
}
